import { memo } from 'react';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';
import { Text } from '@/components/core';
import { DivProps } from '@/utils';

export type BadgeProps = DivProps &
  ComponentContextProps & {
    label?: string | null;
  } & { borderless?: boolean };

const IBadge = ({ variant, label, borderless, children, className, ...rest }: BadgeProps) => {
  const context = useComponent({ variant });

  /* prettier-ignore */
  const styles = [
    borderless ? '' : 'border',
    'flex',
    'gap-1',
    'group/badge',
    'items-center',
    'justify-center',
    'px-1',
    'py-0.5',
    'rounded-sm',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'bg-primary-25',
    'border-primary-200',
  ];

  /* prettier-ignore */
  const successStyles = [
    'bg-success-25',
    'border-success-100',
  ];

  /* prettier-ignore */
  const warningStyles = [
    'bg-warning-50',
    'border-warning-200',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'bg-error-100',
    'border-error-300',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'bg-gray-100',
    'border-none',
    'rounded-xs',
    'p-xs',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    'bg-gray-50',
    'border-1',
    'border-gray-100',
    'rounded-xs',
    'p-xs',
  ];

  const children_ = label ? (
    <Text className="truncate" slot="description">
      {label}
    </Text>
  ) : (
    children
  );

  return (
    <div
      className={merge(
        styles,
        variant === 'primary' && primaryStyles,
        variant === 'success' && successStyles,
        variant === 'warning' && warningStyles,
        variant === 'error' && errorStyles,
        variant === 'plain' && plainStyles,
        variant === 'secondary' && secondaryStyles,
        variant === 'default' && plainStyles,
        className
      )}
      {...rest}
    >
      {renderWithContext(children_, context)}
    </div>
  );
};

export const Badge = memo(IBadge) as typeof IBadge;
